import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { GridValueFormatterParams } from '@mui/x-data-grid';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as api from '../../api/app/performanceCommentCategoryApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { PerformanceCommentCategory } from '../../types/app/peformanceCommentCategory.types';
import { dateTimeShowFormat } from '../../types/core/date.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  parentId: number;
  isReadOnly: boolean;
}
export function PerformanceCommentCategoryList({ parentId, isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const {
    data: commentCategoryList,
    isLoading,
    isError,
  } = useQuery<PerformanceCommentCategory[]>(['commentCategoryList', parentId], () =>
    api.getList(parentId),
  );
  const { mutate: mutateDeletePerformanceCommentCategory } = useMutation(api.deleteItem);

  const columns = [
    { headerName: 'Name', field: 'name', width: 200 },
    { headerName: 'Ordering index', field: 'orderingIndex', width: 200 },
    {
      headerName: 'Updated',
      field: 'updated',
      width: 200,
      valueFormatter: (params: GridValueFormatterParams) => {
        return params.value
          ? moment(params.value?.toString()).local().format(dateTimeShowFormat)
          : '';
      },
    },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 150,
      hide: isReadOnly,
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={dictionary.EDIT}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={dictionary.DELETE}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () =>
    navigate(`${paths.ASSETS_DETAIL}/${parentId}${paths.PERFORMANCE_COMMENT_CATEGORY_CREATE}`);
  const handleOnEdit = (id: number) =>
    navigate(
      `${paths.ASSETS_DETAIL}/${parentId}${paths.PERFORMANCE_COMMENT_CATEGORY_DETAIL}/${id}`,
    );

  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeletePerformanceCommentCategory(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.PERFORMANCE_COMMENT_CATEGORY_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['commentCategoryList']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !commentCategoryList) return <Loader />;
  return (
    <>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <div style={{ height: '65vh', width: '100%' }}>
            <DataGridTable
              height={'65vh'}
              data={commentCategoryList}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
